import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, UserCircle, Prohibit, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';

export default function UsersPage() {
  const { user: currentUser } = useSaaSAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [resetPw, setResetPw] = useState('');

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const createUser = async () => {
    try {
      await api.post('/users', newUser);
      toast.success('User created');
      setShowAdd(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleUser = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}/${isActive ? 'disable' : 'enable'}`);
      toast.success(`User ${isActive ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async (userId) => {
    if (!resetPw || resetPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.put(`/users/${userId}/reset-password`, { new_password: resetPw });
      toast.success('Password reset');
      setShowResetPw(null);
      setResetPw('');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const roleColor = (role) => ({
    super_admin: 'bg-gray-900 text-white border-gray-900',
    admin: 'bg-blue-50 text-blue-700 border-blue-200',
    staff: 'bg-gray-100 text-gray-700 border-gray-200',
    viewer: 'bg-gray-50 text-gray-500 border-gray-200',
  }[role] || 'bg-gray-50 text-gray-500 border-gray-200');

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} users. Manage internal staff access. Only super_admin and admin roles can create or disable users.</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-black text-white hover:bg-gray-800" data-testid="add-user-btn">
          <Plus size={16} className="mr-2" /> Add User
        </Button>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">User</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Email</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Role</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Status</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Last Login</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="py-12 text-center text-gray-400">Loading...</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <UserCircle size={20} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{u.name || '-'}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm font-mono text-gray-600">{u.email}</td>
                <td className="py-3 px-4"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${roleColor(u.role)}`}>{u.role}</span></td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${u.is_active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {u.is_active !== false ? 'active' : 'disabled'}
                  </span>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <button onClick={() => toggleUser(u.id, u.is_active !== false)} className="text-gray-400 hover:text-gray-900" title={u.is_active !== false ? 'Disable' : 'Enable'} data-testid={`toggle-user-${u.id}`}>
                      <Prohibit size={16} />
                    </button>
                    <button onClick={() => { setShowResetPw(u.id); setResetPw(''); }} className="text-gray-400 hover:text-gray-900" title="Reset Password" data-testid={`reset-pw-${u.id}`}>
                      <ArrowCounterClockwise size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add User</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Name</label>
              <Input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} data-testid="new-user-name" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Email</label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} data-testid="new-user-email" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Password</label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} data-testid="new-user-password" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Role</label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={createUser} className="bg-black text-white hover:bg-gray-800" data-testid="save-user-btn" disabled={!newUser.email || !newUser.password}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">New Password</label>
              <Input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} data-testid="reset-password-input" /></div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowResetPw(null)}>Cancel</Button>
              <Button onClick={() => resetPassword(showResetPw)} className="bg-black text-white hover:bg-gray-800" data-testid="confirm-reset-btn">Reset</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
